import DOMPurify from 'isomorphic-dompurify';
import config from '../config';
import * as log from './log';

// What the rich-text editor (draftjs-to-html) produces, plus a few common
// formatting tags older content may contain. Anything else is removed.
const ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'del',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'ins',
  'li',
  'ol',
  'p',
  's',
  'span',
  'strong',
  'sub',
  'sup',
  'u',
  'ul',
];

// rel isn't kept from the input: fixLink sets it on every link.
const ALLOWED_ATTR = ['href', 'target', 'title'];

// Tags that separate words when the HTML is read as plain text.
const TEXT_BREAK_TAGS = new Set([
  'BLOCKQUOTE',
  'BR',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'LI',
  'OL',
  'P',
  'UL',
]);

const hostOf = url => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (e) {
    return null;
  }
};

const SITE_HOST = hostOf(config.canonicalRootURL);

// Links to another site. Relative links, #anchors, mailto: and tel: aren't.
const isExternal = href => {
  const url = href.trim();
  if (!/^(https?:)?\/\//i.test(url)) return false;
  return hostOf(url.startsWith('//') ? `https:${url}` : url) !== SITE_HOST;
};

// The editor writes target="_self" or target="_blank" on every link. Keep
// those and drop any other target. New-tab links can't reach back into this
// page, and links to other sites are marked as user-written so search engines
// don't count them as LandTrust's endorsement.
const fixLink = node => {
  if (node.tagName !== 'A') return;

  const target = node.getAttribute('target');
  if (target !== '_blank' && target !== '_self') node.removeAttribute('target');

  const rel = [];
  if (target === '_blank') rel.push('noopener', 'noreferrer');
  const href = node.getAttribute('href');
  if (href && isExternal(href)) rel.push('ugc', 'nofollow');

  if (rel.length) node.setAttribute('rel', rel.join(' '));
};

// Pages render the same description or bio many times (re-renders, meta tags
// and structured data), so keep recent results. Bounded so a long-running
// server doesn't grow without limit.
const MAX_CACHED = 200;

const memoize = fn => {
  const cache = new Map();
  return value => {
    if (cache.has(value)) return cache.get(value);

    const result = fn(value);
    if (cache.size >= MAX_CACHED) cache.delete(cache.keys().next().value);
    cache.set(value, result);
    return result;
  };
};

// Fail closed: show nothing rather than unsanitized HTML. Without a working
// DOM, DOMPurify returns its input unchanged. It can also throw, for example
// on the server when markup is nested a few thousand levels deep.
const orEmpty = (fn, code) => value => {
  if (!DOMPurify.isSupported) return '';
  try {
    return fn(value);
  } catch (e) {
    log.error(e, code);
    return '';
  }
};

const sanitize = value => {
  // DOMPurify is one shared instance, so add the hook only for this call.
  // sanitize() is synchronous, so no other call can run while it's attached.
  DOMPurify.addHook('afterSanitizeAttributes', fixLink);
  try {
    return DOMPurify.sanitize(value, { ALLOWED_TAGS, ALLOWED_ATTR });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', fixLink);
  }
};

const memoizedSanitize = memoize(orEmpty(sanitize, 'sanitize-html-failed'));

/**
 * User-written rich text (descriptions, bios, messages) reduced to the
 * formatting tags above. Scripts, event handlers, styles, and unsafe link
 * URLs such as javascript: are removed. Use this, through SafeHtml, for any
 * stored HTML rendered into the page.
 */
export const sanitizeHtml = value => (typeof value === 'string' ? memoizedSanitize(value) : '');

const collectText = (node, parts) => {
  node.childNodes.forEach(child => {
    if (child.nodeType === 3) {
      parts.push(child.nodeValue);
      return;
    }

    const breaksText = TEXT_BREAK_TAGS.has(child.tagName);
    if (breaksText) parts.push(' ');
    collectText(child, parts);
    if (breaksText) parts.push(' ');
  });
  return parts;
};

const toText = value => {
  const body = DOMPurify.sanitize(value, { ALLOWED_TAGS, ALLOWED_ATTR: [], RETURN_DOM: true });
  return collectText(body, [])
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
};

const memoizedToText = memoize(orEmpty(toText, 'strip-html-failed'));

/**
 * Rich text as plain text for meta tags, structured data, and empty checks.
 * The browser's HTML parser (jsdom on the server) decodes entities like &amp;
 * and &nbsp;. Paragraphs, list items and line breaks become spaces; inline
 * formatting like <strong> doesn't split words.
 */
export const stripHtml = value => (typeof value === 'string' ? memoizedToText(value) : '');
